import { useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import type { TerminalClause } from '@atlaskit/jql-ast';
import type { CascadingSelectState } from '@atlassian/jira-jql-builder-basic-cascading-select/src/common/types.tsx';
import type { hydrateJqlBuilderQuery$data } from '@atlassian/jira-relay/src/__generated__/hydrateJqlBuilderQuery.graphql';
import type { ui_jqlBuilder_JQLBuilderUI_hydrationQueryData$data } from '@atlassian/jira-relay/src/__generated__/ui_jqlBuilder_JQLBuilderUI_hydrationQueryData.graphql';
import type { useHydratedValues_cascadeSelectPicker$key } from '@atlassian/jira-relay/src/__generated__/useHydratedValues_cascadeSelectPicker.graphql';
import { findUnambiguousCascadeFieldValue } from '../../../../../utils/get-cascade-field-value/index.tsx';

type Props = {
	fieldId: string;
	clause: TerminalClause | undefined;
	hydratedData: hydrateJqlBuilderQuery$data | ui_jqlBuilder_JQLBuilderUI_hydrationQueryData$data;
};

export const useHydratedValues = ({
	fieldId,
	clause,
	hydratedData,
}: Props): CascadingSelectState | undefined => {
	/* eslint-disable @atlassian/relay/graphql-naming, @atlassian/relay/unused-fields */
	const data = useFragment<useHydratedValues_cascadeSelectPicker$key>(
		graphql`
			fragment useHydratedValues_cascadeSelectPicker on Query {
				jira @include(if: $includeJira) {
					jqlBuilder(cloudId: $cloudId) {
						hydrateJqlQuery(query: $jql, viewContext: $viewContext, scope: $scope)
							@skip(if: $isFilter) {
							... on JiraJqlHydratedQuery {
								fields {
									... on JiraJqlQueryHydratedField {
										jqlTerm
										values {
											... on JiraJqlQueryHydratedValue {
												__typename
												jqlTerm
												values {
													... on JiraJqlCascadingOptionFieldValue {
														__typename
														jqlTerm
														displayName
														optionId
														parentOption {
															displayName
															jqlTerm
															optionId
														}
													}
												}
											}
											... on JiraJqlQueryHydratedError {
												__typename
												jqlTerm
											}
										}
									}
								}
							}
						}
						hydrateJqlQueryForFilter(id: $filterAri, viewContext: $viewContext, scope: $scope)
							@include(if: $isFilter) {
							... on JiraJqlHydratedQuery {
								fields {
									... on JiraJqlQueryHydratedField {
										jqlTerm
										values {
											... on JiraJqlQueryHydratedValue {
												__typename
												jqlTerm
												values {
													... on JiraJqlCascadingOptionFieldValue {
														__typename
														jqlTerm
														displayName
														optionId
														parentOption {
															displayName
															jqlTerm
															optionId
														}
													}
												}
											}
											... on JiraJqlQueryHydratedError {
												__typename
												jqlTerm
											}
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		hydratedData,
	);
	/* eslint-enable @atlassian/relay/graphql-naming, @atlassian/relay/unused-fields */

	return useMemo(() => {
		const hydratedValues = (
			data.jira?.jqlBuilder?.hydrateJqlQuery ?? data.jira?.jqlBuilder?.hydrateJqlQueryForFilter
		)?.fields?.find((field) => field.jqlTerm?.toLowerCase() === fieldId)?.values;
		const unambiguousCascadeFieldValue = findUnambiguousCascadeFieldValue({
			clause,
			hydratedValues,
		});

		return unambiguousCascadeFieldValue;
	}, [
		clause,
		data.jira?.jqlBuilder?.hydrateJqlQuery,
		data.jira?.jqlBuilder?.hydrateJqlQueryForFilter,
		fieldId,
	]);
};
